/**
 * Feature Request System - Type Definitions
 *
 * Complete type definitions for the Feature Request System including
 * requests, comments, activity logs, analytics, and form data.
 *
 * @module types/featureRequest
 */

// import { BaseEntity } from './common';

/**
 * Request Type - Categories of requests
 */
export type RequestType = 'feature' | 'bug' | 'change' | 'feedback';

/**
 * Request Status - Lifecycle states
 */
export type RequestStatus =
  | 'new'
  | 'under-review'
  | 'in-progress'
  | 'completed'
  | 'wont-do'
  | 'needs-info'
  | 'duplicate';

/**
 * Priority Levels
 */
export type UserPriority = 'low' | 'medium' | 'high';
export type DeveloperPriority = 'low' | 'medium' | 'high' | 'critical';

/**
 * Screenshot attached to a feature request
 */
export interface Screenshot {
  id: string;
  url: string;
  storagePath: string;
  uploadedAt: string;
  caption?: string;
  fileName: string;
  fileSize: number;
}

/**
 * Main Feature Request Entity
 */
export interface FeatureRequest {
  id: string;

  // User-provided data
  type: RequestType;
  title: string;
  description: string;
  userPriority: UserPriority;
  screenshots: Screenshot[];

  // User information
  submittedBy: string;              // User ID if logged in, or 'anonymous'
  submittedByName?: string;         // Name if provided
  submittedByEmail?: string;        // Email for notifications

  // Status tracking
  status: RequestStatus;
  developerPriority?: DeveloperPriority;

  // Developer notes
  developerNotes?: string;          // Private notes (admin only)
  publicResponse?: string;          // Visible to user

  // Metadata
  createdAt: string;
  updatedAt: string;
  completedAt?: string;

  // Organization
  tags?: string[];
  assignedToMilestone?: string;
  relatedGitHubIssue?: string;
  linkedRequestId?: string;         // For duplicates

  // Counts
  commentCount: number;
  screenshotCount: number;

  // Display helpers
  isUnread?: boolean;               // User hasn't seen latest developer response
}

/**
 * Comment on a feature request
 */
export interface RequestComment {
  id: string;
  requestId: string;
  authorId: string;
  authorName: string;
  authorRole: 'user' | 'developer' | 'admin';
  content: string;
  createdAt: string;
  updatedAt?: string;
  isPrivate: boolean;                // Developer-only comments
  isEdited: boolean;
}

/**
 * Activity log entry for audit trail
 */
export interface RequestActivity {
  id: string;
  requestId: string;
  type: 'status_change' | 'comment' | 'priority_change' | 'assignment' | 'creation' | 'screenshot_added';
  description: string;
  performedBy: string;
  performedByName: string;
  performedByRole: 'user' | 'developer' | 'admin';
  createdAt: string;
  metadata?: Record<string, any>;   // Additional context (e.g., old/new values)
}

/**
 * Form data for creating a new request
 */
export interface RequestFormData {
  type: RequestType;
  title: string;
  description: string;
  userPriority: UserPriority;
  email?: string;
  screenshots?: File[];
}

/**
 * Filters for querying requests
 */
export interface RequestFilters {
  status?: RequestStatus | RequestStatus[];
  type?: RequestType | RequestType[];
  userPriority?: UserPriority;
  developerPriority?: DeveloperPriority;
  submittedBy?: string;
  searchQuery?: string;
  dateRange?: {
    start?: string;
    end?: string;
  };
  hasScreenshots?: boolean;
  hasComments?: boolean;
  sortBy?: 'createdAt' | 'updatedAt' | 'priority' | 'commentCount';
  sortOrder?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
}

/**
 * Analytics data for dashboard
 */
export interface RequestAnalytics {
  totalRequests: number;
  byType: Record<RequestType, number>;
  byStatus: Record<RequestStatus, number>;
  byUserPriority: Record<UserPriority, number>;
  byDeveloperPriority: Record<DeveloperPriority, number>;
  averageResolutionTime: number;    // In days
  mostActiveUsers: Array<{
    userId: string;
    userName: string;
    requestCount: number;
  }>;
  recentActivity: RequestActivity[];
  completionRate: number;           // Percentage
  responseRate: number;             // Percentage with developer response
}

/**
 * Status badge configuration
 */
export interface StatusBadgeConfig {
  label: string;
  color: string;
  icon: string;
  description: string;
}

/**
 * Status badge configurations
 */
export const STATUS_BADGES: Record<RequestStatus, StatusBadgeConfig> = {
  'new': {
    label: 'New',
    color: '#757575',
    icon: '🆕',
    description: 'Just submitted, awaiting review'
  },
  'under-review': {
    label: 'Under Review',
    color: '#2196F3',
    icon: '📋',
    description: 'Developer is reviewing'
  },
  'in-progress': {
    label: 'In Progress',
    color: '#FFC107',
    icon: '🚧',
    description: 'Currently being worked on'
  },
  'completed': {
    label: 'Completed',
    color: '#4CAF50',
    icon: '✅',
    description: 'Implemented and deployed'
  },
  'wont-do': {
    label: "Won't Do",
    color: '#F44336',
    icon: '❌',
    description: 'Not planned for implementation'
  },
  'needs-info': {
    label: 'Needs Info',
    color: '#FF9800',
    icon: '💬',
    description: 'More information needed'
  },
  'duplicate': {
    label: 'Duplicate',
    color: '#9E9E9E',
    icon: '🔗',
    description: 'Duplicate of another request'
  }
};

/**
 * Request type configurations
 */
export interface RequestTypeConfig {
  label: string;
  color: string;
  icon: string;
  description: string;
}

export const REQUEST_TYPES: Record<RequestType, RequestTypeConfig> = {
  'feature': {
    label: 'Feature Request',
    color: '#2196F3',
    icon: '✨',
    description: 'Request a new feature or enhancement'
  },
  'bug': {
    label: 'Bug Report',
    color: '#F44336',
    icon: '🐛',
    description: 'Report something that is not working'
  },
  'change': {
    label: 'Change Request',
    color: '#FF9800',
    icon: '🔄',
    description: 'Request a change to existing functionality'
  },
  'feedback': {
    label: 'General Feedback',
    color: '#4CAF50',
    icon: '💭',
    description: 'General comments or suggestions'
  }
};

/**
 * Priority configurations
 */
export interface PriorityConfig {
  label: string;
  color: string;
  description: string;
}

export const USER_PRIORITIES: Record<UserPriority, PriorityConfig> = {
  'low': {
    label: 'Nice to have',
    color: '#4CAF50',
    description: 'Would be nice but not urgent'
  },
  'medium': {
    label: 'Would really help',
    color: '#FF9800',
    description: 'Would significantly improve my experience'
  },
  'high': {
    label: 'Really need this',
    color: '#F44336',
    description: 'Critical for my workflow'
  }
};

export const DEVELOPER_PRIORITIES: Record<DeveloperPriority, PriorityConfig> = {
  'low': {
    label: 'Low',
    color: '#4CAF50',
    description: 'Nice to have, low impact'
  },
  'medium': {
    label: 'Medium',
    color: '#2196F3',
    description: 'Moderate importance'
  },
  'high': {
    label: 'High',
    color: '#FF9800',
    description: 'Important, should prioritize'
  },
  'critical': {
    label: 'Critical',
    color: '#F44336',
    description: 'Urgent, blocking issue'
  }
};

/**
 * Validation constants
 */
export const VALIDATION_RULES = {
  title: {
    minLength: 5,
    maxLength: 100
  },
  description: {
    minLength: 10,
    maxLength: 2000
  },
  screenshots: {
    maxCount: 3,
    maxSizeBytes: 5 * 1024 * 1024, // 5MB
    allowedTypes: ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp']
  },
  comment: {
    minLength: 1,
    maxLength: 1000
  }
};

/**
 * Helper type guards
 */
export const isValidRequestType = (value: string): value is RequestType => {
  return ['feature', 'bug', 'change', 'feedback'].includes(value);
};

export const isValidRequestStatus = (value: string): value is RequestStatus => {
  return ['new', 'under-review', 'in-progress', 'completed', 'wont-do', 'needs-info', 'duplicate'].includes(value);
};

export const isValidUserPriority = (value: string): value is UserPriority => {
  return ['low', 'medium', 'high'].includes(value);
};

export const isValidDeveloperPriority = (value: string): value is DeveloperPriority => {
  return ['low', 'medium', 'high', 'critical'].includes(value);
};
